import { useState } from 'react'
import { useChampionWeek, runState, actorLabel, ago } from './useChampionWeek.js'
import { formatHeldDay } from './weekDate.js'
import styles from './WeekBoard.module.css'

/* ========================================================================= *
 * WEEK BOARD — the Monday morning answer, above everything else.
 *
 * Brian: "it's Monday, there was a big weekend. Did the 7am search trigger,
 * and how do all the weekend's championships look?"
 *
 * This renders from ONE request. The board underneath it still loads the full
 * universe for the map and the rep books, but the question you actually open
 * the page to ask is answered before that finishes.
 *
 * THE RECEIPT IS THE POINT. An empty week and a dead scheduled job look
 * identical on a board — both show nothing. They have completely different
 * fixes, so this refuses to render them the same way: a window with records
 * and no detection stamps says the job did not fire, in red, rather than
 * quietly showing an empty list and letting everyone assume a slow weekend.
 * ========================================================================= */

const RECEIPT = {
  worked: (d) => `Detection ran ${ago(d.lastDetectionAt)} · ${d.recordsStamped} checked`,
  quiet: (d) => `Detection ran ${ago(d.lastDetectionAt)} · nothing here to check`,
  silent: () => 'The sweep has not run on these records',
  unknown: () => 'Nothing concluded in this window',
}

const BUCKET = {
  crowned: ['Crowned', styles.tCrowned],
  locked: ['Confirmed', styles.tLocked],
  searched_unresolved: ['No source', styles.tSearched],
  untouched: ['Not searched', styles.tUntouched],
}

function Row({ r, onOpen }) {
  const [label, cls] = BUCKET[r.bucket] || BUCKET.untouched
  const held = formatHeldDay(r.heldAt)
  const who = r.lastSearch ? actorLabel(r.lastSearch.by, r.lastSearch.byKind) : null
  /* ⚠️ THESE ROWS WERE NOT CLICKABLE. Brian: "I would be able to click on
   * these." Every other surface on the board opens the record; this panel —
   * the first thing on the page, and the one showing what just concluded —
   * was a dead list. `notReady` covers the honest case where the row is real
   * but the full universe is still paging in, so the click has nothing to open
   * yet: it says so rather than doing nothing. */
  const [notReady, setNotReady] = useState(false)
  const open = () => {
    if (!onOpen) return
    const ok = onOpen(r.id)
    if (!ok) { setNotReady(true); setTimeout(() => setNotReady(false), 2600) }
  }

  return (
    <li
      className={styles.row}
      data-clickable={onOpen ? '1' : '0'}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen ? open : undefined}
      onKeyDown={onOpen ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() } } : undefined}
    >
      <div className={styles.name}>
        {r.name || 'Untitled championship'}
        <span>
          {[r.sport, r.gender, r.state].filter(Boolean).join(' · ')}
          {r.dateEstimated ? ' · date estimated' : ''}
        </span>
      </div>

      <div className={styles.meta}>{held}</div>

      <div className={styles.champ}>
        {r.champion ? (
          <>
            <b>{r.champion}</b>
            {r.finalScore ? <span className={styles.search}>{r.finalScore}</span> : null}
          </>
        ) : (
          <span className={styles.open}>no champion yet</span>
        )}
        {/* WHO looked, and when. A timestamp alone cannot tell you whether the
            engine swept this overnight or a colleague is working it right now —
            identical stamps, opposite next moves. */}
        {r.lastSearch ? (
          <div className={styles.search}>
            searched {ago(r.lastSearch.at)}
            {who ? <> by <em>{who}</em></> : null}
            {r.lastSearch.attempts > 1 ? ` · ${r.lastSearch.attempts} attempts` : ''}
          </div>
        ) : null}
      </div>

      <span className={`${styles.tag} ${cls}`}>{label}</span>
      {notReady && (
        <div className={styles.notReady}>
          Still loading the full record — give it a moment and tap again.
        </div>
      )}
    </li>
  )
}

export default function WeekBoard({
  onOpen,
  /* ⚠️ THIS PANEL IS GLOBAL; SOME SPACES ARE NOT.
   *
   * `/api/champion-week` answers for the WHOLE championship object, and this
   * rendered unscoped in every championship-sourced space. Bowl Season (45
   * college-football records) and Postseason Runs (68) therefore both opened,
   * above the fold and before any of their own content, with the identical
   * three rows: Ohsweken Speedway sprint-car track championships, Motorsports,
   * Canada — under a header reading COLLEGE FOOTBALL.
   *
   * A space that is a SUBSET passes the ids it contains; rows outside them are
   * dropped. Null means "this space is the whole object" (the Championships
   * space), which renders exactly as before. */
  scopeIds = null, scopeLabel = '', scopeReady = true,
}) {
  const [days, setDays] = useState(3)
  const { data, status, error, reload } = useChampionWeek(days)

  const counts = data?.counts || {}
  const allRecords = data?.records || []
  /* Scoping is by id against the space's own universe. While that universe is
   * still paging we show NOTHING rather than the unscoped set — a wrong list is
   * worse than a late one, and this panel sits at the top of the page. */
  const scoped = scopeIds ? allRecords.filter((r) => scopeIds.has(String(r.id))) : allRecords
  const records = scopeIds && !scopeReady ? [] : scoped
  const state = runState(data?.detection, counts.concluded || 0)
  const receipt = (RECEIPT[state] || RECEIPT.unknown)(data?.detection || {})

  return (
    <section className={styles.wrap} aria-label="Recently concluded championships">
      <div className={styles.head}>
        <div>
          <p className={styles.title}>Just concluded</p>
          <p className={styles.sub}>
            {status === 'loading'
              ? 'Checking…'
              : `Last ${days} days · one request, not the universe${scopeIds ? ` · scoped to ${scopeLabel || 'this space'}` : ''}`}
          </p>
        </div>

        <span className={`${styles.receipt} ${styles[state]}`} title={data?.detection?.lastDetectionAt || ''}>
          <span className={styles.dot} />
          {status === 'loading' ? 'Checking…' : receipt}
        </span>

        <div className={styles.spacer} />

        <div className={styles.range}>
          {[3, 7, 14, 30].map((d) => (
            <button key={d} type="button" data-on={d === days ? '1' : '0'} onClick={() => setDays(d)}>
              {d}d
            </button>
          ))}
        </div>
        <button type="button" className={styles.btn} onClick={reload}>Refresh</button>
      </div>

      {status === 'error' ? (
        <p className={styles.err}>{error}</p>
      ) : (
        <>
          {/* The counts come from the global endpoint, so in a scoped space
              they are re-derived from the scoped rows. Showing "3 concluded"
              above zero rendered rows is the same contradiction in a smaller
              font. */}
          <div className={styles.counts}>
            {scopeIds ? (
              <>
                <span className={styles.count}><b>{records.length}</b>concluded</span>
                <span className={styles.count}><b>{records.filter((r) => r.bucket === 'crowned' || r.bucket === 'locked').length}</b>crowned</span>
                <span className={styles.count}><b>{records.filter((r) => r.bucket === 'searched_unresolved').length}</b>no source found</span>
                <span className={styles.count}><b>{records.filter((r) => r.bucket === 'untouched').length}</b>not yet searched</span>
              </>
            ) : (
              <>
                <span className={styles.count}><b>{counts.concluded ?? '—'}</b>concluded</span>
                <span className={styles.count}><b>{(counts.crowned ?? 0) + (counts.locked ?? 0)}</b>crowned</span>
                <span className={styles.count}><b>{counts.searched_unresolved ?? 0}</b>no source found</span>
                <span className={styles.count}><b>{counts.untouched ?? 0}</b>not yet searched</span>
              </>
            )}
          </div>

          {scopeIds && !scopeReady ? (
            <p className={styles.empty}>
              Loading {scopeLabel || 'this space'}’s records — this list is scoped to them.
            </p>
          ) : status === 'done' && !records.length ? (
            /* An empty window is a real answer, but only once the receipt says
               the sweep actually ran. If it did not, the receipt above is red
               and this line must not imply a quiet weekend. */
            <p className={styles.empty}>
              {scopeIds
                ? `Nothing in ${scopeLabel || 'this space'} concluded in this window.`
                : state === 'silent'
                  ? 'Records concluded in this window but nothing has searched them.'
                  : 'Nothing concluded in this window.'}
            </p>
          ) : (
            <ul className={styles.rows}>
              {records.map((r) => <Row key={r.id} r={r} onOpen={onOpen} />)}
            </ul>
          )}
        </>
      )}
    </section>
  )
}

